/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `paymentFormatting` for the Caintra Connect frontend.
 * @module utils/payment/paymentFormatting
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../logger.js";

/**
 * Splits a full name into first name and last name components.
 * @param {string} fullName - The full name to split
 * @returns {{userName: string, userLastName: string}}
 */
export const splitFullName = (fullName) => {
  if (!fullName || typeof fullName !== "string") {
    return { userName: "", userLastName: "" };
  }

  const nameParts = fullName.trim().split(" ");

  if (nameParts.length === 1) {
    return { userName: nameParts[0], userLastName: "" };
  }
  if (nameParts.length === 2) {
    return { userName: nameParts[0], userLastName: nameParts[1] };
  }

  const userName = nameParts[0];
  const userLastName = nameParts.slice(1).join(" ");
  return { userName, userLastName };
};

/**
 * Formats an ISO date string to [DD/MM/YYYY] format for display.
 * @param {string} dateString - ISO date string to format
 * @returns {string} Formatted date string or placeholder if invalid
 */
export const formatDueDate = (dateString) => {
  if (!dateString) {
    return "[DD/MM/AAAA]";
  }

  try {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `[${day}/${month}/${year}]`;
  } catch (error) {
    logger.error("Error formatting date:", { err: sanitizeError(error) });
    return "[DD/MM/AAAA]";
  }
};

/**
 * Formats the current date to DD-MM-YYYY format.
 * @param {Date} [referenceDate=new Date()] - Date to format
 * @returns {string} Current date formatted as DD-MM-YYYY
 */
export const formatCurrentDate = (referenceDate = new Date()) => {
  const day = String(referenceDate.getDate()).padStart(2, "0");
  const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
  const year = referenceDate.getFullYear();
  return `${day}-${month}-${year}`;
};
